import React from 'react'
import { useHistory, useLocation } from 'react-router'
import { AuthForm, AuthFormSubmitValues } from '@local/do-secundo-auth-form'
import { LoginPrompt } from '@local/do-secundo-identity-prompts'
import { PoweredByToastModal } from '../../../../client/components/PoweredByToastModal/PoweredByToastModal'
import { loginValidationSchema } from '../../../../client/utils/form-schemas'
import { ModeLink } from '../../../../client/components/ModeRouter/ModeRouter'
import {
  useIsAccountLinkingEnabled,
  hasExistingLegacyAccount,
  hasPasswordlessAccount
} from '@local/do-secundo-passwordless-authentication'
import {
  deleteTokens,
  accountLinkingStorage
} from '../../../../client/apollo/authentication/authentication-helpers'
export interface LoginPasswordlessModalProps {
  onClose: () => void
  buttonText: string
  includeEmail?: boolean
  handleSubmitError?: (error: { Error: string }) => void
}

export const LoginPasswordlessModal = ({
  onClose,
  handleSubmitError,
  buttonText = 'Next',
  includeEmail = false
}: LoginPasswordlessModalProps) => {
  const history = useHistory()
  const ACCOUNT_LINKING_ENABLED = useIsAccountLinkingEnabled()
  const { state = {} } = useLocation()
  const handleSubmit = async (values: AuthFormSubmitValues) => {
    history.push('?mode=sms', { from: 'login', guestPhone: values.phone })
  }
  const phone = state.guestPhone || state.basicInfo?.phone || ''

  // we need to delete accountLinkingStorage because they may have abandoned
  // the account linking flow previously and now their credentials are out of date
  // and will result in throwing an InvalidJWT.
  React.useEffect(() => {
    deleteTokens(accountLinkingStorage)
  }, [])

  // if a user has an existing legacy account
  // show guests a familiar login prompt, mfa, resetPassword, etc
  // then show them the passwordless login
  if (
    hasExistingLegacyAccount() &&
    ACCOUNT_LINKING_ENABLED &&
    !hasPasswordlessAccount() &&
    state.from !== 'legacyLogin'
  ) {
    history.push('?mode=connectLogin', {
      accountLinkingEnabled: ACCOUNT_LINKING_ENABLED
    })
  }

  const footer = hasPasswordlessAccount() ? (
    <div className={'pt-4 text-center'}>
      Don't have an account?{' '}
      <ModeLink
        data-testid='login-passwordless-modal-create-account'
        style='primary'
        mode='create'
      >
        Create account
      </ModeLink>
    </div>
  ) : (
    <div className={'pt-4 text-center'}>
      <span
        onClick={() => {
          if (ACCOUNT_LINKING_ENABLED) {
            history.push('?mode=connectLogin', {
              accountLinkingEnabled: ACCOUNT_LINKING_ENABLED,
              phone
            })
          } else {
            window.localStorage.setItem('auth', JSON.stringify({}))
          }
        }}
      >
        <ModeLink
          data-testid='login-passwordless-modal-legacy-login'
          style='primary'
          mode='login'
        >
          Log in with email and password
        </ModeLink>
      </span>
      <p>or</p>
      <ModeLink
        data-testid='login-passwordless-modal-create-account'
        style='primary'
        mode='create'
      >
        Create an account
      </ModeLink>
    </div>
  )

  return (
    <PoweredByToastModal
      onClose={onClose}
      header={
        <>
          <h2 className='mx-0 mt-0 mb-3 type-headline-4'>Log In</h2>
          <p className='type-default'>
            See past orders, get your favorites again, and save all your info in
            one place.
          </p>
        </>
      }
    >
      <div className='m-auto ' data-testid='sign-up-prompt-container'>
        <LoginPrompt
          form={
            <AuthForm
              handleSubmit={handleSubmit}
              initialValues={{ phone }}
              validationSchema={loginValidationSchema}
              buttonText={buttonText}
              includeEmail={includeEmail}
              handleSubmitError={handleSubmitError}
              testId={'passwordless-login'}
            />
          }
          footer={footer}
        />
      </div>
    </PoweredByToastModal>
  )
}
